import React, { useState } from 'react';
import { fetchCrabSuppliers } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Crab Suppliers (Azure exit WS8). Reads sql01.CrabSuppliers. Saving is off until
 * ws8_crab_suppliers_live. Not the Accounting Vendors page, which holds different records.
 */

const FILTERS = [['name', 'Name'], ['address', 'Address'], ['city', 'City'], ['state', 'State'], ['zip', 'Zip'], ['phone', 'Phone'], ['email', 'Email'], ['url', 'URL'], ['note', 'Notes']];
const blankForm = { id: null, name: '', address: '', address2: '', city: '', state: '', zip: '', contact: '', phone: '', phone2: '', email: '', url: '', note: '' };
const link = (url) => (url.indexOf('http') === 0 ? url : `http://${url}`);
const noNulls = (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v == null && k !== 'id' ? '' : v]));

export default function CrabSuppliers() {
  const live = useWriteLive('ws8_crab_suppliers');
  const [f, setF] = useState({});
  const [edit, setEdit] = useState(null);
  const { rows, error, reload } = useRows(() => fetchCrabSuppliers(f), [f]);
  const { save, saving, error: saveError, setError } = useSave(reload);

  const input = (key, label, max) => <Field label={label}><input className="form-input" maxLength={max} value={edit[key]} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} /></Field>;
  const submit = async () => {
    if (!edit.name.trim() || !edit.address.trim() || !edit.state.trim() || !edit.phone.trim()) return setError('Name, Address, State and Phone are required.');
    if (await save('crab_supplier_save', edit)) setEdit(null);
    return null;
  };

  return (
    <div>
      <PageTitle>Crab Suppliers</PageTitle>
      <WriteOffNote live={live} what="Add and edit" />
      <div className="port-filters">
        {FILTERS.map(([key, label]) => (
          <input key={key} className="form-input" style={{ width: 130 }} placeholder={label} aria-label={label} value={f[key] || ''} onChange={(e) => setF({ ...f, [key]: e.target.value })} />
        ))}
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => { setError(null); setEdit(blankForm); }}>Add Crab Supplier</button>
      </div>
      <ListCard title="Suppliers" rows={rows} error={error} empty="No suppliers.">
        <table className="trans-table">
          <thead><tr><th>State</th><th>Name</th><th>Address</th><th>Contact</th><th>URL</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id}>
                <td>{r.state}</td>
                <td>{r.name}{r.note && <div className="port-note">Notes: {r.note}</div>}</td>
                <td>{r.address}{r.address2 && <><br />{r.address2}</>}{(r.city || r.state || r.zip) && <><br />{r.city} {r.state} {r.zip}</>}</td>
                <td>{r.contact}{r.phone && <><br />{r.phone}</>}{r.phone2 && <><br />{r.phone2}</>}{r.email && <><br /><a href={`mailto:${r.email}`}>{r.email}</a></>}</td>
                <td>{r.url && <a href={link(r.url)} target="_blank" rel="noreferrer">{r.url}</a>}</td>
                <td className="staff-actions"><button type="button" className="btn btn-ghost" disabled={!live || saving} onClick={() => { setError(null); setEdit(noNulls(r)); }}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {edit && (
        <EditDialog title={edit.id ? 'Edit Crab Supplier' : 'Add Crab Supplier'} live={live} saving={saving} error={saveError} onClose={() => setEdit(null)} onSave={submit}>
          <div className="staff-grid">
            {input('name', 'Name*', 50)}
            {input('contact', 'Contact', 50)}
            {input('address', 'Address*', 255)}
            {input('address2', 'Address 2', 255)}
            {input('city', 'City', 50)}
            {input('state', 'State*', 50)}
            {input('zip', 'Zip', 50)}
            {input('phone', 'Phone*', 50)}
            {input('phone2', 'Phone 2', 50)}
            {input('email', 'Email', 50)}
            {input('url', 'Url', 255)}
          </div>
          <Field label="Notes"><textarea className="form-textarea" maxLength={500} value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} /></Field>
        </EditDialog>
      )}
    </div>
  );
}
